import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

const API = (path, opts = {}) => {
  const token = localStorage.getItem('crm_token');
  return axios({ url: path, headers: { Authorization: `Bearer ${token}` }, ...opts });
};

function POS() {
  const [tab, setTab] = useState('cash'); // cash | sale | receivables | report
  const [loading, setLoading] = useState(true);

  // Kasa session
  const [session, setSession] = useState(null);
  const [openingBalance, setOpeningBalance] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [closingBalance, setClosingBalance] = useState('');

  // Randevu ödemesi
  const [appointments, setAppointments] = useState([]);
  const [selectedApt, setSelectedApt] = useState(null);
  const [aptPaymentMethods, setAptPaymentMethods] = useState([{ method: 'cash', amount: 0 }]);

  // Serbest satış
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saleItems, setSaleItems] = useState([{ type: 'manual', name: '', unit_price: 0, quantity: 1 }]);
  const [salePaymentMethods, setSalePaymentMethods] = useState([{ method: 'cash', amount: 0 }]);
  const [saleDiscount, setSaleDiscount] = useState(0);
  const [products, setProducts] = useState([]);

  // Finansal özet
  const [todaySummary, setTodaySummary] = useState({ total_revenue: 0, cash: 0, card: 0, transfer: 0 });
  const [commissions, setCommissions] = useState([]);
  const [receivables, setReceivables] = useState([]);

  // Fiş modal
  const [receiptData, setReceiptData] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [sessRes, aptRes, sumRes, commRes, recRes, prodRes] = await Promise.allSettled([
        API('/api/pos/sessions/current'),
        API(`/api/appointments?date=${today}&status=completed`),
        API('/api/pos/today-summary'),
        API(`/api/pos/staff-commissions?date=${today}`),
        API('/api/pos/receivables'),
        API('/api/products?limit=200'),
      ]);

      if (sessRes.status === 'fulfilled') setSession(sessRes.value.data.session);
      if (aptRes.status === 'fulfilled') setAppointments(aptRes.value.data);
      if (sumRes.status === 'fulfilled') setTodaySummary(sumRes.value.data);
      if (commRes.status === 'fulfilled') setCommissions(commRes.value.data);
      if (recRes.status === 'fulfilled') setReceivables(recRes.value.data);
      if (prodRes.status === 'fulfilled') setProducts(Array.isArray(prodRes.value.data) ? prodRes.value.data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Müşteri arama
  useEffect(() => {
    if (customerSearch.length < 2) { setCustomers([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await API(`/api/customers?search=${encodeURIComponent(customerSearch)}&limit=10`);
        setCustomers(Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.customers || []));
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // ── Kasa Aç/Kapat ──
  const handleOpenSession = async () => {
    try {
      await API('/api/pos/sessions/open', { method: 'POST', data: { opening_balance: parseFloat(openingBalance) || 0, notes: sessionNotes } });
      setOpeningBalance(''); setSessionNotes('');
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Kasa açılamadı'); }
  };

  const handleCloseSession = async () => {
    if (!window.confirm('Kasayı kapatmak istediğinize emin misiniz?')) return;
    try {
      const res = await API('/api/pos/sessions/close', { method: 'POST', data: { closing_balance: parseFloat(closingBalance) || 0 } });
      const { difference } = res.data;
      alert(`Kasa kapatıldı. Fark: ${difference > 0 ? '+' : ''}${parseFloat(difference).toLocaleString('tr-TR')} ₺`);
      setClosingBalance('');
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Kasa kapatılamadı'); }
  };

  // ── Randevu Ödemesi ──
  const handleAptPayment = async () => {
    if (!selectedApt) return;
    const totalAmount = parseFloat(selectedApt.final_price || 0);
    const paid = aptPaymentMethods.reduce((s, m) => s + parseFloat(m.amount || 0), 0);
    if (paid < totalAmount) {
      if (!window.confirm(`Eksik ödeme (${paid} ₺). Kısmi ödeme olarak kaydet?`)) return;
    }
    try {
      const res = await API('/api/payments', {
        method: 'POST',
        data: {
          appointment_id: selectedApt.id,
          customer_id: selectedApt.customer_id,
          total_amount: selectedApt.price || totalAmount,
          discount_amount: selectedApt.discount_amount || 0,
          net_amount: totalAmount,
          payment_methods: aptPaymentMethods
        }
      });
      alert('Ödeme alındı!');
      setSelectedApt(null);
      setAptPaymentMethods([{ method: 'cash', amount: 0 }]);
      // Fiş göster
      try {
        const rRes = await API(`/api/pos/receipt/${res.data.id}`);
        setReceiptData(rRes.data); setShowReceipt(true);
      } catch {}
      fetchAll();
    } catch { alert('Ödeme başarısız'); }
  };

  // ── Serbest Satış ──
  const calcSaleTotal = () => {
    const gross = saleItems.reduce((s, i) => s + parseFloat(i.unit_price || 0) * parseFloat(i.quantity || 1), 0);
    return Math.max(0, gross - parseFloat(saleDiscount || 0));
  };

  const handleSale = async () => {
    if (!selectedCustomer) return alert('Müşteri seçiniz');
    if (!saleItems.some(i => i.name && parseFloat(i.unit_price) > 0)) return alert('En az 1 ürün/hizmet giriniz');
    try {
      const res = await API('/api/pos/sale', {
        method: 'POST',
        data: {
          customer_id: selectedCustomer.id,
          items: saleItems,
          payment_methods: salePaymentMethods,
          discount_amount: parseFloat(saleDiscount) || 0
        }
      });
      alert(`Satış tamamlandı! ${res.data.loyalty_points_earned} sadakat puanı kazanıldı.`);
      setSaleItems([{ type: 'manual', name: '', unit_price: 0, quantity: 1 }]);
      setSalePaymentMethods([{ method: 'cash', amount: 0 }]);
      setSaleDiscount(0);
      setSelectedCustomer(null);
      setCustomerSearch('');
      try {
        const rRes = await API(`/api/pos/receipt/${res.data.payment.id}`);
        setReceiptData(rRes.data); setShowReceipt(true);
      } catch {}
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Satış başarısız'); }
  };

  // ── Alacak Tahsil ──
  const handleCollect = async (receivable) => {
    const amount = window.prompt(`Tahsil edilecek tutar (Kalan: ${parseFloat(receivable.net_amount).toLocaleString('tr-TR')} ₺):`);
    if (!amount) return;
    const method = window.prompt('Ödeme yöntemi (cash/card/transfer):') || 'cash';
    try {
      await API(`/api/pos/receivables/${receivable.id}/collect`, {
        method: 'POST',
        data: { payment_methods: [{ method, amount: parseFloat(amount) }] }
      });
      alert('Tahsilat kaydedildi');
      fetchAll();
    } catch (e) { alert(e.response?.data?.error || 'Hata'); }
  };

  if (loading) return <div style={{ padding: 40, color: '#fff', textAlign: 'center' }}>Yükleniyor...</div>;

  const grandTotal = (todaySummary.cash || 0) + (todaySummary.card || 0) + (todaySummary.transfer || 0);

  const tabBtnStyle = (t) => ({
    padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
    background: tab === t ? 'rgba(0,242,254,0.18)' : 'rgba(255,255,255,0.05)',
    color: tab === t ? '#00f2fe' : 'rgba(255,255,255,0.5)',
    transition: 'all 0.2s'
  });

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title">💰 Kasa / POS Terminali</h1>
        {/* Kasa Session Durumu */}
        <div style={{
          padding: '8px 18px', borderRadius: 20,
          background: session ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${session ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          color: session ? '#10b981' : '#ef4444', fontWeight: 600, fontSize: 13
        }}>
          {session ? `🟢 Kasa Açık — ${parseFloat(session.opening_balance).toLocaleString('tr-TR')} ₺ ile açıldı` : '🔴 Kasa Kapalı'}
        </div>
      </div>

      {/* ── KPI Kartları ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Bugünkü Toplam', val: grandTotal, color: '#3B82F6', icon: '💰' },
          { label: 'Nakit', val: todaySummary.cash || 0, color: '#10B981', icon: '💵' },
          { label: 'Kart', val: todaySummary.card || 0, color: '#8B5CF6', icon: '💳' },
          { label: 'Havale/EFT', val: todaySummary.transfer || 0, color: '#F59E0B', icon: '🏦' }
        ].map((item, idx) => (
          <div key={idx} className="card" style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: item.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
              {item.icon}
            </div>
            <div>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#fff' }}>{parseFloat(item.val).toLocaleString('tr-TR')} ₺</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{item.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Sekme Navigasyonu ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <button style={tabBtnStyle('cash')} onClick={() => setTab('cash')}>💳 Randevu Ödemesi</button>
        <button style={tabBtnStyle('sale')} onClick={() => setTab('sale')}>🛍 Serbest Satış</button>
        <button style={tabBtnStyle('receivables')} onClick={() => setTab('receivables')}>🕒 Alacak Takibi ({receivables.length})</button>
        <button style={tabBtnStyle('report')} onClick={() => setTab('report')}>📊 Prim Raporu</button>
        <button style={tabBtnStyle('session')} onClick={() => setTab('session')}>⚙️ Kasa Yönetimi</button>
      </div>

      {/* ── RANDEVU ÖDEMESI ── */}
      {tab === 'cash' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div className="card" style={{ padding: 24 }}>
            <h3 style={{ fontWeight: 700, marginBottom: 16, color: '#fff' }}>Tamamlanan Randevular</h3>
            {appointments.length === 0 ? (
              <p className="text-muted">Bugün ödeme bekleyen tamamlanmış randevu yok.</p>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {appointments.map(apt => (
                  <div key={apt.id} onClick={() => { setSelectedApt(apt); setAptPaymentMethods([{ method: 'cash', amount: parseFloat(apt.final_price || 0) }]); }}
                    style={{
                      padding: 14, borderRadius: 10,
                      border: selectedApt?.id === apt.id ? '2px solid #00f2fe' : '1px solid rgba(255,255,255,0.08)',
                      background: selectedApt?.id === apt.id ? 'rgba(0,242,254,0.07)' : 'rgba(255,255,255,0.03)',
                      cursor: 'pointer', transition: 'all 0.2s'
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ color: '#fff' }}>{apt.customer_first_name} {apt.customer_last_name}</strong>
                      <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{parseFloat(apt.final_price || 0).toLocaleString('tr-TR')} ₺</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                      ✂️ {apt.service_name} &nbsp;•&nbsp; 👤 {apt.staff_first_name} {apt.staff_last_name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 24 }}>
            <h3 style={{ fontWeight: 700, marginBottom: 16, color: '#fff' }}>Ödeme Al</h3>
            {!selectedApt ? (
              <p className="text-muted">Soldan randevu seçin.</p>
            ) : (
              <div>
                <div style={{ marginBottom: 12, color: 'rgba(255,255,255,0.7)' }}>
                  <strong style={{ color: '#fff', display: 'block' }}>{selectedApt.customer_first_name} {selectedApt.customer_last_name}</strong>
                  <span style={{ fontSize: 13 }}>{selectedApt.service_name}</span>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#10b981', marginBottom: 20 }}>
                  {parseFloat(selectedApt.final_price || 0).toLocaleString('tr-TR')} ₺
                </div>
                {aptPaymentMethods.map((pm, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <select className="input" value={pm.method} onChange={e => { const u = [...aptPaymentMethods]; u[i].method = e.target.value; setAptPaymentMethods(u); }} style={{ width: 130 }}>
                      <option value="cash">💵 Nakit</option>
                      <option value="card">💳 Kart</option>
                      <option value="transfer">🏦 Havale</option>
                    </select>
                    <input type="number" className="input" value={pm.amount} onChange={e => { const u = [...aptPaymentMethods]; u[i].amount = e.target.value; setAptPaymentMethods(u); }} />
                    {aptPaymentMethods.length > 1 && <button className="btn btn-danger" style={{ padding: '0 12px' }} onClick={() => setAptPaymentMethods(aptPaymentMethods.filter((_, j) => j !== i))}>×</button>}
                  </div>
                ))}
                <button className="btn btn-secondary btn-sm" onClick={() => setAptPaymentMethods([...aptPaymentMethods, { method: 'cash', amount: 0 }])} style={{ marginBottom: 16 }}>+ Yöntem Ekle</button>
                <button className="btn btn-primary" style={{ width: '100%', padding: 14, fontWeight: 700 }} onClick={handleAptPayment}>Tahsilatı Tamamla</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── SERBEST SATIŞ ── */}
      {tab === 'sale' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Sol: Müşteri + Ürünler */}
          <div className="card" style={{ padding: 24 }}>
            <h3 style={{ fontWeight: 700, marginBottom: 16, color: '#fff' }}>Ürün / Hizmet Seç</h3>

            {/* Müşteri Arama */}
            <div style={{ marginBottom: 16 }}>
              <label className="form-label">Müşteri</label>
              {selectedCustomer ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(var(--primary-rgb),0.08)', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(var(--primary-rgb),0.2)' }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{selectedCustomer.first_name} {selectedCustomer.last_name} — {selectedCustomer.phone}</span>
                  <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 700 }}>×</button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <input className="input" placeholder="Ad, telefon ara..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
                  {customers.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, zIndex: 50, maxHeight: 200, overflowY: 'auto' }}>
                      {customers.map(c => (
                        <div key={c.id} onClick={() => { setSelectedCustomer(c); setCustomers([]); setCustomerSearch(''); }} style={{ padding: '10px 14px', cursor: 'pointer', color: '#fff', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          {c.first_name} {c.last_name} — {c.phone}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Ürün/Hizmet Satırları */}
            <label className="form-label" style={{ marginBottom: 8 }}>Kalemler</label>
            {saleItems.map((item, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 70px 70px auto', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                <select className="input" value={item.type} onChange={e => { const u = [...saleItems]; u[i].type = e.target.value; if (e.target.value !== 'manual') u[i].name = ''; setSaleItems(u); }} style={{ fontSize: 11, padding: '8px 4px' }}>
                  <option value="manual">Manuel</option>
                  <option value="product">Ürün</option>
                </select>
                {item.type === 'product' ? (
                  <select className="input" value={item.id || ''} onChange={e => { const p = products.find(p => p.id === parseInt(e.target.value)); const u = [...saleItems]; u[i] = { ...u[i], id: p?.id, name: p?.name || '', unit_price: p?.price || 0 }; setSaleItems(u); }} style={{ fontSize: 12 }}>
                    <option value="">Ürün seç</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name} — {p.price} ₺</option>)}
                  </select>
                ) : (
                  <input className="input" placeholder="Açıklama" value={item.name} onChange={e => { const u = [...saleItems]; u[i].name = e.target.value; setSaleItems(u); }} style={{ fontSize: 12 }} />
                )}
                <input type="number" className="input" placeholder="Fiyat" value={item.unit_price} onChange={e => { const u = [...saleItems]; u[i].unit_price = e.target.value; setSaleItems(u); }} style={{ fontSize: 12 }} />
                <input type="number" className="input" placeholder="Adet" value={item.quantity} min="1" onChange={e => { const u = [...saleItems]; u[i].quantity = e.target.value; setSaleItems(u); }} style={{ fontSize: 12 }} />
                {saleItems.length > 1 && <button onClick={() => setSaleItems(saleItems.filter((_, j) => j !== i))} style={{ background: 'rgba(239,68,68,0.15)', border: 'none', color: '#ef4444', borderRadius: 8, cursor: 'pointer', padding: '0 8px', height: 36, fontSize: 16 }}>×</button>}
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={() => setSaleItems([...saleItems, { type: 'manual', name: '', unit_price: 0, quantity: 1 }])}>+ Kalem Ekle</button>

            <div style={{ marginTop: 16 }}>
              <label className="form-label">İskonto (₺)</label>
              <input type="number" className="input" value={saleDiscount} onChange={e => setSaleDiscount(e.target.value)} style={{ width: 120 }} />
            </div>
          </div>

          {/* Sağ: Ödeme */}
          <div className="card" style={{ padding: 24 }}>
            <h3 style={{ fontWeight: 700, marginBottom: 16, color: '#fff' }}>Ödeme</h3>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#10b981', marginBottom: 20 }}>
              {calcSaleTotal().toLocaleString('tr-TR')} ₺
            </div>
            {salePaymentMethods.map((pm, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select className="input" value={pm.method} onChange={e => { const u = [...salePaymentMethods]; u[i].method = e.target.value; setSalePaymentMethods(u); }} style={{ width: 130 }}>
                  <option value="cash">💵 Nakit</option>
                  <option value="card">💳 Kart</option>
                  <option value="transfer">🏦 Havale</option>
                </select>
                <input type="number" className="input" value={pm.amount} onChange={e => { const u = [...salePaymentMethods]; u[i].amount = e.target.value; setSalePaymentMethods(u); }} />
                {salePaymentMethods.length > 1 && <button className="btn btn-danger" style={{ padding: '0 12px' }} onClick={() => setSalePaymentMethods(salePaymentMethods.filter((_, j) => j !== i))}>×</button>}
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={() => setSalePaymentMethods([...salePaymentMethods, { method: 'cash', amount: 0 }])} style={{ marginBottom: 20 }}>+ Yöntem Ekle</button>
            <button className="btn btn-primary" style={{ width: '100%', padding: 14, fontWeight: 700 }} onClick={handleSale}>Satışı Tamamla</button>
          </div>
        </div>
      )}

      {/* ── ALACAK TAKİBİ ── */}
      {tab === 'receivables' && (
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 20, color: '#fff' }}>🕒 Alacak Takibi</h3>
          {receivables.length === 0 ? (
            <p className="text-muted">Bekleyen alacak kaydı yok.</p>
          ) : (
            <table className="table" style={{ fontSize: 13 }}>
              <thead>
                <tr><th>Müşteri</th><th>Hizmet</th><th>Tutar</th><th>Gecikme</th><th>Durum</th><th>İşlem</th></tr>
              </thead>
              <tbody>
                {receivables.map(r => (
                  <tr key={r.id}>
                    <td><strong>{r.customer_first_name} {r.customer_last_name}</strong><br /><span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{r.customer_phone}</span></td>
                    <td>{r.service_name || 'Manuel Satış'}</td>
                    <td style={{ color: '#ef4444', fontWeight: 700 }}>{parseFloat(r.net_amount).toLocaleString('tr-TR')} ₺</td>
                    <td><span style={{ color: r.overdue_days > 30 ? '#ef4444' : '#f59e0b' }}>{r.overdue_days} gün</span></td>
                    <td><span className={`badge badge-${r.status === 'pending' ? 'danger' : 'warning'}`}>{r.status === 'pending' ? 'Gecikmiş' : 'Kısmi'}</span></td>
                    <td><button className="btn btn-primary btn-sm" onClick={() => handleCollect(r)}>Tahsil Et</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── PRİM RAPORU ── */}
      {tab === 'report' && (
        <div className="card" style={{ padding: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 20, color: '#fff' }}>👤 Bugünkü Personel Prim Raporu</h3>
          {commissions.length === 0 ? (
            <p className="text-muted">Bugün kaydedilen prim bulunmuyor.</p>
          ) : (
            <table className="table" style={{ fontSize: 13 }}>
              <thead>
                <tr><th>Personel</th><th>İşlem</th><th>Hasılat</th><th>Prim %</th><th>Kazanılan Prim</th></tr>
              </thead>
              <tbody>
                {commissions.map(c => (
                  <tr key={c.staff_id}>
                    <td><strong>{c.first_name} {c.last_name}</strong></td>
                    <td>{c.total_services}</td>
                    <td>{parseFloat(c.total_revenue).toLocaleString('tr-TR')} ₺</td>
                    <td>%{c.commission_rate}</td>
                    <td style={{ color: '#10b981', fontWeight: 700 }}>{parseFloat(c.total_commission).toLocaleString('tr-TR')} ₺</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── KASA YÖNETİMİ ── */}
      {tab === 'session' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {!session ? (
            <div className="card" style={{ padding: 24 }}>
              <h3 style={{ fontWeight: 700, marginBottom: 16, color: '#fff' }}>🔓 Kasa Aç</h3>
              <div className="form-group">
                <label className="form-label">Açılış Bakiyesi (₺)</label>
                <input type="number" className="input" value={openingBalance} onChange={e => setOpeningBalance(e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Not</label>
                <input type="text" className="input" value={sessionNotes} onChange={e => setSessionNotes(e.target.value)} placeholder="Opsiyonel" />
              </div>
              <button className="btn btn-primary" style={{ width: '100%', padding: 14, fontWeight: 700 }} onClick={handleOpenSession}>Kasayı Aç</button>
            </div>
          ) : (
            <div className="card" style={{ padding: 24 }}>
              <h3 style={{ fontWeight: 700, marginBottom: 16, color: '#fff' }}>🔒 Kasa Kapat</h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginBottom: 16 }}>
                Açılış bakiyesi: <strong style={{ color: '#fff' }}>{parseFloat(session.opening_balance).toLocaleString('tr-TR')} ₺</strong>
              </p>
              <div className="form-group">
                <label className="form-label">Sayılan Kapanış Bakiyesi (₺)</label>
                <input type="number" className="input" value={closingBalance} onChange={e => setClosingBalance(e.target.value)} placeholder="0" />
              </div>
              <button className="btn btn-danger" style={{ width: '100%', padding: 14, fontWeight: 700 }} onClick={handleCloseSession}>Kasayı Kapat</button>
            </div>
          )}
          <div className="card" style={{ padding: 24 }}>
            <h3 style={{ fontWeight: 700, marginBottom: 16, color: '#fff' }}>📈 Gün Özeti</h3>
            {[
              { label: 'Toplam Ciro', val: grandTotal, color: '#3b82f6' },
              { label: 'Nakit', val: todaySummary.cash, color: '#10b981' },
              { label: 'Kart', val: todaySummary.card, color: '#8b5cf6' },
              { label: 'Havale', val: todaySummary.transfer, color: '#f59e0b' },
              { label: 'İptal/İade', val: todaySummary.refund_amount || 0, color: '#ef4444' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{item.label}</span>
                <span style={{ color: item.color, fontWeight: 700 }}>{parseFloat(item.val || 0).toLocaleString('tr-TR')} ₺</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FİŞ MODALI ── */}
      {showReceipt && receiptData && (
        <div className="modal-overlay" onClick={() => setShowReceipt(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, fontFamily: 'monospace' }}>
            <div className="modal-header">
              <h3 className="modal-title">🧾 Fiş / Makbuz</h3>
              <button className="close-btn" onClick={() => setShowReceipt(false)}>×</button>
            </div>
            <div style={{ padding: 20, background: 'rgba(0,0,0,0.3)', borderRadius: 8, margin: '0 0 12px' }}>
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <strong style={{ fontSize: 16, color: '#fff' }}>{receiptData.branch?.name}</strong><br />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{receiptData.branch?.address}</span>
              </div>
              <div style={{ borderTop: '1px dashed rgba(255,255,255,0.2)', margin: '8px 0' }} />
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 2 }}>
                <div>Fiş No: <strong style={{ color: '#fff' }}>{receiptData.receipt_no}</strong></div>
                <div>Tarih: {receiptData.date ? format(new Date(receiptData.date), 'dd/MM/yyyy HH:mm') : '-'}</div>
                <div>Müşteri: {receiptData.customer?.name}</div>
                <div>Hizmet: {receiptData.service}</div>
                {receiptData.staff && <div>Personel: {receiptData.staff}</div>}
              </div>
              <div style={{ borderTop: '1px dashed rgba(255,255,255,0.2)', margin: '8px 0' }} />
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Toplam:</span><span>{receiptData.total_amount?.toLocaleString('tr-TR')} ₺</span>
                </div>
                {receiptData.discount_amount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#10b981' }}>
                    <span>İskonto:</span><span>-{receiptData.discount_amount?.toLocaleString('tr-TR')} ₺</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: 'var(--primary)', fontSize: 14 }}>
                  <span>Tahsil:</span><span>{receiptData.net_amount?.toLocaleString('tr-TR')} ₺</span>
                </div>
              </div>
              {receiptData.payment_methods?.length > 0 && (
                <>
                  <div style={{ borderTop: '1px dashed rgba(255,255,255,0.2)', margin: '8px 0' }} />
                  {receiptData.payment_methods.map((m, i) => (
                    <div key={i} style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', display: 'flex', justifyContent: 'space-between' }}>
                      <span>{m.method === 'cash' ? 'Nakit' : m.method === 'card' ? 'Kart' : 'Havale'}:</span>
                      <span>{parseFloat(m.amount).toLocaleString('tr-TR')} ₺</span>
                    </div>
                  ))}
                </>
              )}
              <div style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Teşekkür ederiz!</div>
            </div>
            <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => window.print()}>🖨 Yazdır</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default POS;
